import {
  Button,
  ModalBackground,
  Notification
} from 'bloomer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { setOfflineMode } from '../actions/offlineActions'
import api from '../api'
import { AlphaprocessDatabase } from '../offlineMode/offlineDb'
import { SERVER_REACHABLE_EVENT } from '../offlineMode/serverReachable'
import { IAlphaprocessState, store } from '../store'
import { confirm } from '../util'
import LoadSpinner from './loadSpinner/LoadSpinner'

(window as any).enableOfflineMode = () => {
  store.dispatch(setOfflineMode(true, true))
}

const RECONNECT_TIMEOUT_MS = 8000
// how often the app looks for the server while it is offline by itself
const RECONNECT_INTERVAL_MS = 10000

// the app files are on the device (service worker, src/serviceWorker): a reload without connection
// starts the app again, and unsent answers wait in local storage (offlineMode/pendingResults)
const appStartsOffline = () => !!navigator.serviceWorker?.controller

export default function OfflineIndicator() {
  const isOffline = useSelector((state: IAlphaprocessState) => state.core.isOffline)
  const offlineManual = useSelector((state: IAlphaprocessState) => !!state.core.offlineManual)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(false)
  // The connection check currently in flight. A click on "Connect" while the automatic
  // check is running joins that check instead of being ignored.
  const pendingCheck = useRef<Promise<boolean> | null>(null)

  const onReconnected = useCallback(async () => {
    const db = new AlphaprocessDatabase()

    const dirty = await db.resultSets.filter((x) => x._dirty).toArray()
    if (dirty.length) {
      confirm(null, t('offlineMode.confirmSyncAll'), async () => {
        setBusy(true)
        for (const rs of dirty) {
          await db.syncChecklist(rs, t, () => {})
          await db.resultSets.delete(rs.id)
        }
        toast(t('offlineMode.bulkSyncSuccess'), { type: 'success' })
        setBusy(false)
        window.location.reload()
      })
    }
  }, [t])

  // Resolves to true if the server is reachable.
  const checkConnection = useCallback((): Promise<boolean> => {
    if (!pendingCheck.current) {
      pendingCheck.current = api.ping(RECONNECT_TIMEOUT_MS)
        .then(() => true, () => false)
        .finally(() => {
          pendingCheck.current = null
        })
    }
    return pendingCheck.current
  }, [])

  const reconnect = useCallback(async (manual: boolean) => {
    if (manual) {
      setChecking(true)
    }
    const reachable = await checkConnection()
    if (manual) {
      setChecking(false)
    }

    // isOffline may have changed while waiting (e.g. the automatic check already reconnected)
    if (!store.getState().core.isOffline) {
      return
    }

    if (reachable) {
      dispatch(setOfflineMode(false))
      toast(t('offlineMode.reconnected'), { type: 'success' })
      await onReconnected()
    } else if (manual) {
      toast(
        t(navigator.onLine === false ? 'offlineMode.reconnectFailedNoInternet' : 'offlineMode.reconnectFailed'),
        { type: 'warning', toastId: 'offline-reconnect-failed' }
      )
    }
  }, [checkConnection, dispatch, onReconnected, t])

  // Offline mode caused by connection errors is left automatically as soon as the
  // server is reachable again. Manually chosen offline mode stays until the user reconnects.
  useEffect(() => {
    if (!isOffline || offlineManual) {
      return undefined
    }

    const check = () => {
      if (document.visibilityState === 'visible') {
        reconnect(false)
      }
    }
    const timer = window.setInterval(check, RECONNECT_INTERVAL_MS)
    window.addEventListener('online', check)
    window.addEventListener(SERVER_REACHABLE_EVENT, check)
    document.addEventListener('visibilitychange', check)
    check()

    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', check)
      window.removeEventListener(SERVER_REACHABLE_EVENT, check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [isOffline, offlineManual, reconnect])

  const handleReconnectClicked = useCallback(() => {
    reconnect(true)
  }, [reconnect])

  // leaving the page offline only loses the app where it is not on the device (old native app, dev server)
  useEffect(() => {
    if (isOffline && !appStartsOffline()) {
      window.onbeforeunload = () => t('offlineMode.confirmExit')
    } else {
      window.onbeforeunload = null
    }
  }, [isOffline, t])

  if (busy) {
    return (
      <ModalBackground>
        <LoadSpinner className="centered" />
      </ModalBackground>
    )
  }

  if (isOffline) {
    return (
      <Notification isColor="warning" className="offline-indicator">
        <div className="indicator-text">
          { t(offlineManual ? 'offlineMode.currentOffline' : 'offlineMode.connectionLost') }
        </div>
        <Button
          isSize="small"
          isPulled={isMobileOnly ? null : 'right'}
          onClick={handleReconnectClicked}
          isLoading={checking}
          disabled={checking}
        >
          { t('offlineMode.reconnect') }
        </Button>
      </Notification>
    )
  }
  return null
}
